import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Buildings, Download, ArrowSquareOut, Receipt, Truck, Wrench, ShoppingCart, FileText, ChartBar, Warning } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TITLES = {
  "quotation": "Quotation",
  "invoice": "Tax Invoice",
  "receipt": "Payment Receipt",
  "purchase-order": "Purchase Order",
  "delivery-challan": "Delivery Challan",
  "installation-completion": "Installation Completion Certificate",
  "customer-statement": "Customer Statement",
};

const ICONS = {
  "quotation": FileText,
  "invoice": Receipt,
  "receipt": Receipt,
  "purchase-order": ShoppingCart,
  "delivery-challan": Truck,
  "installation-completion": Wrench,
  "customer-statement": ChartBar,
};

const formatINR = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

const PublicDocPage = () => {
  const { docType, token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/${docType}/${token}`);
        if (mounted) setData(r.data);
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail || "Could not load document";
        setError(status === 410 ? "This link has been revoked by the sender. Please contact them for a new link." :
                 status === 404 ? "Link not found. It may have been removed or the URL is incorrect." :
                 detail);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [docType, token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-slate-500 text-sm">Loading document…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div data-testid="public-doc-error" className="bg-white border border-slate-200 max-w-md w-full p-8 shadow-sm text-center">
          <Warning size={48} weight="duotone" className="text-amber-500 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>Document Unavailable</h2>
          <p className="text-slate-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const Icon = ICONS[docType] || FileText;
  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}${data.pdf_url}`;
  const lineItems = data?.doc?.line_items || data?.doc?.items || [];

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            {data.company?.logo_url ? (
              <img src={data.company.logo_url} alt={data.company.name} className="h-8" />
            ) : (
              <Buildings size={24} className="text-[#002FA7]" />
            )}
            <div>
              <div className="text-base font-semibold text-slate-900" style={{fontFamily: 'Outfit, sans-serif'}}>{data.company?.name}</div>
              {data.company?.gstin && <div className="text-[10px] uppercase tracking-wider text-slate-500">GSTIN {data.company.gstin}</div>}
            </div>
          </div>
          <div className="flex gap-2">
            <a
              data-testid="public-pdf-download"
              href={pdfUrl}
              target="_blank"
              rel="noreferrer"
              className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-4 py-2 text-sm font-medium inline-flex items-center gap-2 transition-colors"
            >
              <Download size={14} weight="bold" /> Download PDF
            </a>
            <a
              data-testid="public-pdf-open"
              href={pdfUrl}
              target="_blank"
              rel="noreferrer"
              className="rounded-none border border-slate-300 hover:bg-slate-50 text-slate-800 px-4 py-2 text-sm font-medium inline-flex items-center gap-2 transition-colors"
            >
              <ArrowSquareOut size={14} /> Open in new tab
            </a>
          </div>
        </div>
      </div>

      {/* Document card */}
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div data-testid="public-doc-card" className="bg-white border border-slate-200 shadow-sm">
          <div className="border-b border-slate-200 px-8 py-6 flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#002FA7]/10 flex items-center justify-center">
                <Icon size={22} weight="bold" className="text-[#002FA7]" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{TITLES[docType] || docType}</div>
                <h1 data-testid="public-doc-number" className="text-3xl font-bold tracking-tight" style={{fontFamily: 'Outfit, sans-serif'}}>
                  {data.doc_number || "—"}
                </h1>
              </div>
            </div>
            {data.amount > 0 && (
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Amount</div>
                <div data-testid="public-doc-amount" className="text-3xl font-bold text-[#002FA7]" style={{fontFamily: 'Outfit, sans-serif'}}>
                  {formatINR(data.amount)}
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-8 py-6">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Customer / Vendor</div>
              <div data-testid="public-doc-customer" className="text-base font-semibold text-slate-900">{data.customer_name || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Document Date</div>
              <div className="text-base text-slate-900">{(data.doc_date || "").slice(0, 10) || "—"}</div>
            </div>
            {data.company?.phone && (
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">From</div>
                <div className="text-sm text-slate-700">{data.company.name}</div>
                <div className="text-xs text-slate-600">{data.company.phone}</div>
                {data.company.email && <div className="text-xs text-slate-600">{data.company.email}</div>}
              </div>
            )}
            {data.company?.address && (
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Address</div>
                <div className="text-xs text-slate-600 whitespace-pre-wrap">{data.company.address}</div>
              </div>
            )}
          </div>

          {/* Line items */}
          {lineItems.length > 0 && (
            <div className="border-t border-slate-200 overflow-x-auto">
              <table data-testid="public-doc-items" className="w-full">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">#</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Description</th>
                    <th className="text-right p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Qty</th>
                    <th className="text-right p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Rate</th>
                    <th className="text-right p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((li, i) => {
                    const qty = Number(li.quantity || 0);
                    const rate = Number(li.rate || li.unit_price || 0);
                    const lineTotal = Number(li.line_total || 0) || (qty * rate);
                    return (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="p-3 text-sm">{i + 1}</td>
                        <td className="p-3 text-sm whitespace-pre-wrap break-words align-top">{li.description || li.product_name || "—"}</td>
                        <td className="p-3 text-sm text-right tabular-nums">{qty}</td>
                        <td className="p-3 text-sm text-right tabular-nums">{formatINR(rate)}</td>
                        <td className="p-3 text-sm text-right tabular-nums font-semibold">{formatINR(lineTotal)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Customer statement special view */}
          {docType === "customer-statement" && (
            <div className="border-t border-slate-200 px-8 py-6 space-y-6">
              {(data.doc?.invoices || []).length > 0 && (
                <div>
                  <h3 className="text-sm uppercase tracking-[0.2em] font-bold text-slate-700 mb-2">Invoices</h3>
                  <table className="w-full">
                    <thead><tr className="bg-slate-50"><th className="text-left p-2 text-xs">Invoice #</th><th className="text-left p-2 text-xs">Date</th><th className="text-right p-2 text-xs">Amount</th></tr></thead>
                    <tbody>
                      {data.doc.invoices.map((i, idx) => (
                        <tr key={idx} className="border-t border-slate-100">
                          <td className="p-2 text-sm">{i.doc_number || "—"}</td>
                          <td className="p-2 text-sm">{(i.created_at || "").slice(0, 10)}</td>
                          <td className="p-2 text-sm text-right tabular-nums">{formatINR(i.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {(data.doc?.payments || []).length > 0 && (
                <div>
                  <h3 className="text-sm uppercase tracking-[0.2em] font-bold text-slate-700 mb-2">Payments</h3>
                  <table className="w-full">
                    <thead><tr className="bg-slate-50"><th className="text-left p-2 text-xs">Date</th><th className="text-left p-2 text-xs">Method</th><th className="text-right p-2 text-xs">Amount</th></tr></thead>
                    <tbody>
                      {data.doc.payments.map((p, idx) => (
                        <tr key={idx} className="border-t border-slate-100">
                          <td className="p-2 text-sm">{(p.paid_on || "").slice(0, 10)}</td>
                          <td className="p-2 text-sm">{p.method || "—"}</td>
                          <td className="p-2 text-sm text-right tabular-nums">{formatINR(p.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="border-t border-slate-200 px-8 py-4 text-center">
            <p className="text-xs text-slate-500">This is a secure shared document. Need help? Reach out to {data.company?.name} at {data.company?.phone || data.company?.email || "the contact above"}.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicDocPage;
